import {
  attr,
  css,
  customElement,
  ExecutionContext,
  GenesisElement,
  html,
  observable,
  repeat,
  ViewTemplate,
  volatile,
  when,
} from '@genesislcap/web-core';
import { cancelButtonBg } from '../../../../styles';
import { MaturityOrder } from '../deal-series.types';
import { formatMillisecondsToMonthYear, formatToThreeDecimals } from '../deal-series.util';
import { customEvent } from '@genesislcap/foundation-events';

const formatMonthYear = (value: number): string => {
  const { month, year } = formatMillisecondsToMonthYear(value);
  return `${month}/${year}`;
};

const rowHeight = '24px';

const getMuniDetailStrip = (muniOrder: MaturityOrder): ViewTemplate => html`
  <span>${() => muniOrder.SERIES_CODE || ''}</span>
  <span>${() => formatMonthYear(muniOrder.MATURITY_DATE)}</span>
  <span>${() => formatToThreeDecimals(muniOrder.COUPON)}</span>
  <span>${() => formatToThreeDecimals(muniOrder.YIELD)}</span>
  <span>${() => formatToThreeDecimals(muniOrder.PRICE)}</span>
`;

@customElement({
  name: 'munis-multi-select',
  template: html<MunisMultiSelect>`
      <div class="munis-multi-select list">
        <div class="close-button-container">
          <rapid-icon 
            @click="${x => x.closeList()}" 
            name="xmark" 
            class="close-button"
            title="Close">
          </rapid-icon>
        </div>
        <div class="list-container">
          ${repeat(x => x.sortedMunisWithStatus, html<MaturityOrderWithStatus>`
            <div class="summary-option">
              <rapid-checkbox 
                ?disabled="${(x) => x.disabled}"
                @change="${(x, ctx: ExecutionContext<MunisMultiSelect>) => ctx.parent.handleChange(customEvent(ctx), x.maturity)}" 
                :checked="${(x, ctx: ExecutionContext<MunisMultiSelect>) => x.maturity.MATURITY_ID === ctx.parent.flexMaturityKey}">
              </rapid-checkbox>
              ${x => getMuniDetailStrip(x.maturity)}
            </div>
          `)}
        </div>
        <div class="list-actions">
          <rapid-button @click="${x => x.resetChanges()}">Reset</rapid-button>
        </div>
      </div>
  `,
  styles: css`
    :host {
      position: relative;
      display: block;
    }

    .munis-multi-select {
      border-radius: 3px;
      background: var(--neutral-fill-rest);
      max-height: calc(${rowHeight} * 4);
      overflow: auto;
    }

    .munis-multi-select.list {
      position: absolute;
      border: 1px solid ${cancelButtonBg};
      top: 0;
      right: 0;
      left: auto;
      min-width: 100%;
      width: max-content;
      max-height: calc(${rowHeight} * 12);
      z-index: 10;
      display: flex;
      flex-direction: column;
      overflow: hidden;

      .close-button-container {
        flex: 0;
        display: flex;
        justify-content: flex-end;
        padding: 4px 8px;
        border-bottom: 1px solid ${cancelButtonBg};
        background: var(--neutral-fill-rest);
      }

      .close-button {
        cursor: pointer;
        padding: 2px;
        border-radius: 2px;
      }

      .list-container {
        flex: 1;
        overflow-y: auto;
      }

      .list-actions {
        flex: 0;
        display: flex;
        border-top: 1px solid ${cancelButtonBg};
        padding: 5px;
        align-items: center;
        justify-content: flex-end;
      }
    }

    .summary-option {
      display: flex;
      align-items: center;
      gap: 10px;
      white-space: nowrap;
      height: ${rowHeight};
      padding: 4px 8px;

      rapid-checkbox::part(label) {
        display: none;
      }

      .disabled-checkbox-placeholder {
        width: 16px;
        height: 16px;
        opacity: 0.3;
      }

      span {
        flex: 0 0 auto;
      }
    }
  `,
})
export class MunisMultiSelect extends GenesisElement {
  @attr key: string = 'MUNI_STRIP';

  @attr({ attribute: 'issuance-key' }) issuanceKey: string;

  @observable munisOrders: MaturityOrder[];

  @observable flexMaturityKey: string;

  private equalsId(a: string | number, b: string | number): boolean {
    const na = Number(a), nb = Number(b);
    return !Number.isNaN(na) && !Number.isNaN(nb) ? na === nb : a === b;
  }
  
  isNeighboringMaturity(
    targetMaturity: MaturityOrder, 
    sortedMaturities: MaturityOrder[], 
    currentIssuanceKey: string | number
  ): boolean {
    if (!currentIssuanceKey || !sortedMaturities?.length) return false;
  
    const currentIndex = sortedMaturities.findIndex(maturityOrder => this.equalsId(maturityOrder.MATURITY_ID, currentIssuanceKey));
    if (currentIndex === -1) return false;
  
    const idx = sortedMaturities.findIndex(maturityOrder => this.equalsId(maturityOrder.MATURITY_ID, targetMaturity.MATURITY_ID));
    return idx === currentIndex - 1 || idx === currentIndex + 1;
  }

  closeList(): void {
    this.$emit('close');
  }
  
  @volatile get sortedMunisWithStatus(): MaturityOrderWithStatus[] {
    if (!this.munisOrders) return [];
    const sorted = [...this.munisOrders].sort((a, b) => a.MATURITY_DATE - b.MATURITY_DATE);
    return sorted.map(maturity => ({
      maturity,
      disabled: this.equalsId(maturity.MATURITY_ID, this.issuanceKey)
    }));
  }

  disconnectedCallback(): void {
    this.munisOrders = null;
    this.flexMaturityKey = null;
    super.disconnectedCallback();
  }

  resetChanges(): void {
    this.flexMaturityKey = null;
    this.$emit('selected-changed', this.flexMaturityKey);
  }

  handleChange(e: CustomEvent, order: MaturityOrder): void {
    const checked = (e.target as HTMLInputElement).checked;
    if (checked) {
      this.flexMaturityKey = order.MATURITY_ID;
    } else if (this.equalsId(order.MATURITY_ID, this.flexMaturityKey)) {
      this.flexMaturityKey = null;
    }
    this.$emit('selected-changed', this.flexMaturityKey);
  }
}

interface MaturityOrderWithStatus {
  maturity: MaturityOrder;
  disabled: boolean;
}
